import { useEffect, useRef, useCallback } from 'react';
import { debugLog } from './debug';
import { captureSettingsForType } from './captureSettings';
import { restoreSettingsForType } from './restoreSettings';
import { useSettingHandlers } from './useSettingHandlers';
import { DEFAULT_VIEW_SETTINGS } from '../../utils/campaignViews/index';

/**
 * useCustomViewSettings Hook
 * 
 * Synchronizes view settings between the active custom view and the component state.
 * - When switching TO a custom view: saves default settings, then restores custom view settings
 * - When switching FROM a custom view: restores the saved default settings
 * - When settings change while a custom view is active: saves to that custom view
 * 
 * Settings managed per view type:
 * 
 * FLOW: viewport, filters, fokusMode, cardDisplaySettings, flowViewVisibility
 * KANBAN: cardDisplaySettings, swimlaneSetting, sortSetting, fokusMode, filters
 * TABLE: fokusMode, sortSetting, filters, columnVisibility
 * GANTT: fokusMode, viewport, filters
 * TODO: fokusMode, viewMode, swimlaneGroupBy, sortSetting, filters, elementVisibility
 * NEURAL: fokusMode, filters, cardDisplaySettings
 */
export function useCustomViewSettings({
  activeCustomView,
  updateView,
  // Current view type (flow, kanban, table, gantt, todolist) - needed even when on default view
  currentViewType,
  // Filter settings
  filtersPerView,
  setFiltersPerView,
  // Sort settings - these come from localSprint, so we need setLocalSprint
  localSprint,
  setLocalSprint,
  // Swimlane settings
  swimlaneSetting,
  // Table column visibility
  tableColumnVisibility,
  setTableColumnVisibility,
  // Todo element visibility
  todoElementVisibility,
  setTodoElementVisibility,
  // Flow view visibility
  flowViewVisibility,
  onFlowViewVisibilityChange,
  // Gantt swimlane setting
  ganttSwimlane,
  setGanttSwimlane,
  // Archive/actionable filters
  showArchived,
  setShowArchived,
  showActionableOnly,
  setShowActionableOnly,
  showNestedCampaigns,
  setShowNestedCampaigns,
  // FOKUS mode per view (Flow uses toolbar, others use useFokusMode)
  toolbar, // For Flow FOKUS state (isFocusMode, focusedTaskIds, handleExitFocusMode)
  flowchartRef, // For Flow FOKUS actions
  kanbanFokus,
  tableFokus,
  ganttFokus,
  todoFokus,
  neuralFokus,
  // Todo view mode
  todoViewMode,
  setTodoViewMode,
  todoSwimlaneGroupBy,
  setTodoSwimlaneGroupBy,
  // New: Todo swimlane setting (same as Kanban)
  todoSwimlane,
  setTodoSwimlane,
  // New: Show nested items toggle
  todoShowNestedItems,
  setTodoShowNestedItems,
  // Flow viewport (optional - for saving pan/zoom)
  flowViewport,
  onFlowViewportChange,
  // Gantt viewport (optional - for saving zoom/scroll)
  ganttViewport,
  onGanttViewportChange,
  // Callback when switching back to default view (to clear local overrides)
  onSwitchToDefault,
  // Default view settings (for saving when no custom view is active)
  updateDefaultViewSettings,
}) {
  // Track the previous active view to detect changes
  const prevActiveViewIdRef = useRef(null);
  // Track the previous active view object (to know its type when switching away)
  const prevActiveViewRef = useRef(null);
  // Track if we're currently restoring settings (to avoid save loops)
  const isRestoringRef = useRef(false);
  // Store default view settings when switching to a custom view
  const defaultSettingsRef = useRef({});
  // Store refs to current values to avoid stale closures
  const currentValuesRef = useRef({});

  // Get the view type for the active custom view
  const viewType = activeCustomView?.type;

  // Keep refs updated with current values (to avoid stale closures)
  useEffect(() => {
    // Create a Flow FOKUS wrapper that matches the interface of other fokus hooks
    const flowFokus = toolbar ? {
      isFokusMode: toolbar.isFocusMode,
      focusedTaskIds: toolbar.focusedTaskIds,
      primaryFokusedIds: toolbar.focusedTaskIds, // Flow doesn't distinguish primary vs all
      // Direct setters for synchronous state updates during view restoration
      setIsFocusMode: toolbar.setIsFocusMode,
      setFocusedTaskIds: toolbar.setFocusedTaskIds,
      exitFokusMode: toolbar.handleExitFocusMode,
      enterFokusMode: (taskIds) => {
        // Flow FOKUS is entered via flowchartRef
        if (flowchartRef?.current?.focusTasks) {
          flowchartRef.current.focusTasks(taskIds, false);
        }
      },
      // Instant methods (no animation - for view switching)
      exitFokusModeInstant: () => {
        if (flowchartRef?.current?.exitFocusModeInstant) {
          flowchartRef.current.exitFocusModeInstant();
        } else {
          toolbar.handleExitFocusMode();
        }
      },
      enterFokusModeInstant: (taskIds) => {
        // Update toolbar state first so initialFocusedTaskIds is correct on next render
        if (toolbar.setIsFocusMode) {
          toolbar.setIsFocusMode(true);
        }
        if (toolbar.setFocusedTaskIds) {
          toolbar.setFocusedTaskIds(new Set(taskIds));
        }
        if (flowchartRef?.current?.focusTasksInstant) {
          flowchartRef.current.focusTasksInstant(taskIds);
        } else if (flowchartRef?.current?.focusTasks) {
          flowchartRef.current.focusTasks(taskIds, false);
        }
      },
    } : null;

    currentValuesRef.current = {
      filtersPerView,
      localSprint,
      tableColumnVisibility,
      todoElementVisibility,
      flowViewVisibility,
      ganttSwimlane,
      showArchived,
      showActionableOnly,
      showNestedCampaigns,
      flowFokus,
      kanbanFokus,
      tableFokus,
      ganttFokus,
      todoFokus,
      neuralFokus,
      todoViewMode,
      todoSwimlaneGroupBy,
      todoSwimlane,
      todoShowNestedItems,
      flowViewport,
      ganttViewport,
    };
  });

  // Capture current settings for a view type
  const captureCurrentSettings = useCallback((type) => {
    return captureSettingsForType(type, currentValuesRef.current);
  }, []);

  // Restore settings wrapper - uses currentValuesRef to avoid stale closures
  const restoreSettings = useCallback((settings, type) => {
    const vals = currentValuesRef.current;
    
    const setters = {
      setFiltersPerView,
      setLocalSprint,
      setTableColumnVisibility,
      setTodoElementVisibility,
      onFlowViewVisibilityChange,
      onFlowViewportChange,
      setGanttSwimlane,
      onGanttViewportChange,
      setShowArchived,
      setShowActionableOnly,
      setShowNestedCampaigns,
      setTodoViewMode,
      setTodoSwimlaneGroupBy,
      setTodoSwimlane,
      setTodoShowNestedItems,
    };

    const fokusHooks = { 
      flowFokus: vals.flowFokus,
      kanbanFokus: vals.kanbanFokus, 
      tableFokus: vals.tableFokus, 
      ganttFokus: vals.ganttFokus, 
      todoFokus: vals.todoFokus,
      neuralFokus: vals.neuralFokus,
    };
    
    restoreSettingsForType(settings, type, setters, fokusHooks, isRestoringRef);
  }, [
    setFiltersPerView, setLocalSprint, setTableColumnVisibility, setTodoElementVisibility,
    onFlowViewVisibilityChange, onFlowViewportChange, setGanttSwimlane, onGanttViewportChange,
    setShowArchived, setShowActionableOnly, setShowNestedCampaigns, setTodoViewMode, setTodoSwimlaneGroupBy,
    setTodoSwimlane, setTodoShowNestedItems,
  ]);

  // Track the previous view type (for default views)
  const prevViewTypeRef = useRef(null);

  // Handle view switching
  useEffect(() => {
    const currentViewId = activeCustomView?.id || null;
    const prevViewId = prevActiveViewIdRef.current;
    const prevView = prevActiveViewRef.current;
    const prevViewType = prevViewTypeRef.current;
    
    // Get the effective view type - use custom view's type if active, otherwise use currentViewType prop
    const effectiveCurrentType = activeCustomView?.type || currentViewType;
    const effectivePrevType = prevView?.type || prevViewType;

    debugLog('View switch check', {
      currentViewId,
      prevViewId,
      currentViewType: effectiveCurrentType,
      prevViewType: effectivePrevType,
      activeCustomViewSettings: activeCustomView?.settings,
    });

    // Only act if the view actually changed (either custom view ID or view type)
    const viewIdChanged = currentViewId !== prevViewId;
    const viewTypeChanged = effectiveCurrentType !== effectivePrevType;
    
    if (!viewIdChanged && !viewTypeChanged) return;

    const prevWasCustomView = prevViewId !== null;
    const currentIsCustomView = currentViewId !== null;

    debugLog('View switch detected', {
      prevWasCustomView,
      currentIsCustomView,
      viewIdChanged,
      viewTypeChanged,
      effectiveCurrentType,
      effectivePrevType,
      defaultSettingsSaved: Object.keys(defaultSettingsRef.current),
    });

    if (currentIsCustomView && activeCustomView) {
      // Switching TO a custom view
      const type = activeCustomView.type;
      
      // ALWAYS capture current default settings when switching FROM default TO custom view
      // This ensures we capture any changes the user made on the default view
      if (!prevWasCustomView) {
        debugLog('Saving default settings for type:', type);
        const capturedDefaults = captureCurrentSettings(type);
        
        // Save the defaults AS-IS (including FOKUS state if active)
        // This allows us to restore the default view's state when switching back
        debugLog('Captured defaults (including current FOKUS state):', capturedDefaults);
        defaultSettingsRef.current[type] = capturedDefaults;
      } else if (prevView && prevView.type !== type) {
        // Switching between custom views of DIFFERENT types
        // Only capture if we don't have defaults for this type yet
        debugLog('Switching to different view type, checking defaults for:', type);
        if (!defaultSettingsRef.current[type]) {
          debugLog('Capturing defaults for new type:', type);
          defaultSettingsRef.current[type] = captureCurrentSettings(type);
        }
      } else {
        debugLog('Switching between custom views of same type, keeping existing defaults');
      }
      
      // Restore the custom view's settings
      if (activeCustomView.settings) {
        debugLog('Restoring custom view settings for:', activeCustomView.name, activeCustomView.settings);
        restoreSettings(activeCustomView.settings, type);
      } else {
        debugLog('Custom view has no settings yet:', activeCustomView.name);
        
        // If this is a newly created custom view with no settings, restore DEFAULT settings
        // Custom views should start with a blank slate, not inherit current state
        const defaultSettings = DEFAULT_VIEW_SETTINGS[type];
        if (defaultSettings) {
          debugLog('Restoring DEFAULT settings for new custom view:', defaultSettings);
          restoreSettings(defaultSettings, type);
        }
      }
    } else if (!currentIsCustomView && prevWasCustomView && prevView) {
      // Switching FROM a custom view back to default
      // Only restore the type that was previously active
      const prevType = prevView.type;
      debugLog('Switching back to default, restoring type:', prevType);
      debugLog('Saved default settings for this type:', defaultSettingsRef.current[prevType]);
      
      // Clear any local overrides (like flowViewVisibility) before restoring
      if (onSwitchToDefault) {
        onSwitchToDefault();
      }
      
      if (defaultSettingsRef.current[prevType]) {
        debugLog('Restoring default settings:', defaultSettingsRef.current[prevType]);
        restoreSettings(defaultSettingsRef.current[prevType], prevType);
      } else {
        debugLog('No saved default settings found for type:', prevType);
      }
    }

    prevActiveViewIdRef.current = currentViewId;
    prevActiveViewRef.current = activeCustomView;
    prevViewTypeRef.current = effectiveCurrentType;
  }, [activeCustomView, currentViewType, captureCurrentSettings, restoreSettings, onSwitchToDefault]);

  // Get all setting handlers
  const handlers = useSettingHandlers({
    activeCustomView,
    updateView,
    isRestoringRef,
    // Default view settings support
    updateDefaultViewSettings,
    currentViewType,
    // Setters
    setFiltersPerView,
    setTableColumnVisibility,
    setTodoElementVisibility,
    onFlowViewVisibilityChange,
    setGanttSwimlane,
    setShowArchived,
    setShowActionableOnly,
    setShowNestedCampaigns,
    setTodoViewMode,
    setTodoSwimlaneGroupBy,
    onFlowViewportChange,
    onGanttViewportChange,
  });

  return {
    // Flag to check if a custom view is active
    isCustomViewActive: !!activeCustomView,
    activeViewType: viewType,
    
    // Spread all handlers
    ...handlers,
  };
}

export default useCustomViewSettings;
